import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'auth'
  | 'authContinue'
  | 'marketing';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variantClassNames: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary:
    'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50',
  ghost: 'text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50',
  danger: 'bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50',
  auth: 'auth-primary-button font-semibold shadow-lg transition-all hover:shadow-xl disabled:cursor-not-allowed',
  authContinue:
    'auth-continue-button font-semibold shadow-lg transition-all disabled:cursor-not-allowed',
  marketing:
    'bg-primary-600 text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50',
};

const variantsWithoutSize: ButtonVariant[] = ['auth', 'authContinue'];

const sizeClassNames: Record<ButtonSize, string> = {
  xs: 'h-8 rounded-lg px-3 text-xs font-medium',
  sm: 'h-9 rounded-lg px-3.5 text-sm font-medium',
  md: 'h-10 rounded-lg px-4 text-sm font-medium',
  lg: 'h-11 rounded-lg px-5 text-base font-medium',
};

export default function Button({
  variant = 'primary',
  size = 'sm',
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 transition-colors',
        variantClassNames[variant],
        !variantsWithoutSize.includes(variant) && sizeClassNames[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}
